
import React, {useEffect, useState} from "react";
import { useApi } from "./UserProvider";
import { getISOWeek } from "utilities/dateAndTime";

export const ProjectsContext = React.createContext({
    projects: [],
    projectsLoading: true,
    handleProjectEdit: () => {},
});

export const ProjectsProvider = ({children}) => {

    const { api } = useApi();
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(true);

    useEffect(() => {
        setProjectsLoading(true);
        const fetchProjects = async () => {
            try
            {
                const prj = await api.request("projects/query/all");
                setProjects(prj);

            }
            catch(err)
            {
                console.error(err)
            }
            finally
            {
                setProjectsLoading(false);
            }
        }
        fetchProjects()
    }, [api]);


    const handleProjectEdit = async ({projectUuid, action, changes}) => {

        let previousProjects = structuredClone(projects);

        if(action === "STATUS CHANGE")
        {
            let projectCopy = structuredClone(projects.find(prj => prj.projectUuid === projectUuid));
            if(!projectCopy) return;

            if(projectCopy.projectActivity.enabled && projectCopy.projectActivity.automaticStatusChange)
            {
                return ({
                    status: "PROJECT_NOT_UPDATED",
                    reason: "PROJECT_STATUS_CONTROLLED_BY_ACTIVITY"
                });
            }

            if(projectCopy.projectStatus === changes.newStatus)
            {
                return ({
                    status: "PROJECT_NOT_UPDATED",
                    reason: "PROJECT_STATUS_IDENTICAL"
                })
            }

            projectCopy.projectStatus = changes.newStatus;

            const isArchived = changes.newStatus === "Archived";

            const nextState = isArchived ? projects.filter(prj => prj.projectUuid !== projectUuid)
                : projects.map(prj => prj.projectUuid !== projectUuid ? prj : projectCopy);

            setProjects(nextState);

            try
            {
                await api.request('projects/update', projectCopy);

                if(isArchived)
                {
                    return ({
                        status: "PROJECT_ARCHIVED",
                        reason: "PROJECT_ARCHIVED"
                    });
                }
                else
                {
                    return ({
                        status: "PROJECT_UPDATED"
                    });
                }
                
            }
            catch(err)
            {
                setProjects(previousProjects);
            }
        }
        else if(action === "EDIT PROJECT")
        {
            let projectCopy = structuredClone(projects.find(prj => prj.projectUuid === projectUuid));
            if(!projectCopy) return;

            projectCopy.projectNumber = changes.projectNumber;
            projectCopy.projectName = changes.projectName;
            projectCopy.projectFolder = changes.projectFolder;
            projectCopy.projectDescription = changes.projectDescription;

            projectCopy.projectActivity = changes.projectActivity;
            projectCopy.projectContent = changes.projectContent;
            projectCopy.projectMap = changes.projectMap;
            projectCopy.projectNotes = changes.projectNotes;
            projectCopy.projectTasks = changes.projectTasks;
            projectCopy.projectDeliveries = changes.projectDeliveries;
            projectCopy.projectPhases = changes.projectPhases;
            projectCopy.projectVersions = changes.projectVersions;

            if(changes.projectActivity.enabled && changes.projectActivity.automaticStatusChange)
            {
                const newStatus = checkProjectActivityStatus(changes.projectActivity.activity);
                projectCopy.projectStatus = newStatus || "Inactive";
            }

            const otherProjects = projects.filter(prj => prj.projectUuid !== projectUuid);
            setProjects([projectCopy, ...otherProjects]);

            const projectCopyApi = structuredClone(projectCopy);

            try
            {
                await api.request("projects/update", projectCopyApi);
                return {
                    status: "PROJECT_UPDATED",
                };
            }
            catch(err)
            {
                setProjects(previousProjects);
                return {
                    status: "PROJECT_NOT_UPDATED",
                    reason: "SERVER_ERROR"
                };
            }
        }
        else if(action === "CREATE PROJECT")
        {
            const projectStatus =
                (changes.projectActivity.enabled && changes.projectActivity.automaticStatusChange)
                ? checkProjectActivityStatus(changes.projectActivity.activity) || "Inactive"
                : "Active";

            const uiProject = {
                projectUuid,
                projectNumber: changes.projectNumber,
                projectName: changes.projectName,
                projectFolder: changes.projectFolder,
                projectStatus,

                projectActivity: changes.projectActivity,
                projectContent: changes.projectContent,
                projectMap: changes.projectMap,
                projectNotes: changes.projectNotes,
                projectTasks: changes.projectTasks,
                projectDeliveries: changes.projectDeliveries,
                projectPhases: changes.projectPhases,
                projectVersions: changes.projectVersions
            };

            const apiProject = {
                projectUuid,
                projectNumber: changes.projectNumber,
                projectName: changes.projectName,
                projectFolder: changes.projectFolder,
                projectDescription: changes.projectDescription,
                projectStatus,

                projectActivity: changes.projectActivity,
                projectContent: changes.projectContent,
                projectMap: changes.projectMap,
                projectNotes: changes.projectNotes,
                projectTasks: changes.projectTasks,
                projectDeliveries: changes.projectDeliveries,
                projectPhases: changes.projectPhases,
                projectVersions: changes.projectVersions
            };

            let newProject = {};

            newProject.projectUuid = projectUuid;
            newProject.projectNumber = changes.projectNumber;
            newProject.projectName = changes.projectName;
            newProject.projectFolder = changes.projectFolder;
            newProject.projectDescription = changes.projectDescription;

            newProject.projectActivity = changes.projectActivity;
            newProject.projectContent = changes.projectContent;
            newProject.projectMap = changes.projectMap;
            newProject.projectNotes = changes.projectNotes;
            newProject.projectTasks = changes.projectTasks;
            newProject.projectDeliveries = changes.projectDeliveries;
            newProject.projectPhases = changes.projectPhases;
            newProject.projectVersions = changes.projectVersions;

            const otherProjects = projects.filter(prj => prj.projectUuid !== projectUuid);
            setProjects([uiProject, ...otherProjects]);

            try
            {
                await api.request("projects/create", apiProject);
                return {
                    status: "PROJECT_CREATED",
                };
            }
            catch (err)
            {
                console.error(err);
                setProjects(previousProjects);

                return {
                    status: "PROJECT_NOT_CREATED",
                    reason: "SERVER_ERROR"
                };
            }
        }
    }

    return (
        <ProjectsContext.Provider value = {{projects: projects, projectsLoading: projectsLoading, handleProjectEdit: handleProjectEdit}}>
            { children }
        </ProjectsContext.Provider>
    )   
}

const checkProjectActivityStatus = (activity) => {
    const today = new Date();
    const code = today.getFullYear() + "W" + getISOWeek(today);

    if(activity[code] && activity[code] >= 1) return 'Active';
    else return 'Inactive';
}
